"use client";

import type { ParentEntity } from "@/contexts/DetailContextProvider.types";

/**
 * Converts parent entity context to GraphQL filter
 * Maps parent entity type and ID to appropriate filter for nested entities
 *
 * @param parentEntity - Parent entity information (contact/company)
 * @param nestedEntityType - Type of nested entity being filtered (tickets, time_entries, tasks, contacts, etc.)
 * @returns GraphQL filter object or null if context is not applicable
 */
export function convertContextToGraphQLFilter(
  parentEntity: ParentEntity | null | undefined,
  nestedEntityType: string
): Record<string, any> | null {
  if (!parentEntity?.id || !parentEntity?.type) {
    return null;
  }

  const { type: parentType, id: parentId } = parentEntity;

  // Map parent entity type and nested entity type to appropriate filter
  switch (parentType) {
    case "contact": {
      // When viewing a contact detail, filter nested entities by contact_id
      switch (nestedEntityType.toLowerCase()) {
        case "tickets":
        case "ticket":
          return { contact_id: { eq: parentId } };

        case "time_entries":
        case "time_entry":
        case "timeentries":
          return { contact_id: { eq: parentId } };

        case "tasks":
        case "task":
          return { contact_id: { eq: parentId } };

        case "notes":
        case "note":
          return { contact_id: { eq: parentId } };

        case "communications":
        case "communication":
          return { contact_id: { eq: parentId } };

        default:
          return null;
      }
    }

    case "company": {
      // When viewing a company detail, filter nested entities by company_id
      switch (nestedEntityType.toLowerCase()) {
        case "contacts":
        case "contact":
          return { company_id: { eq: parentId } };

        case "tickets":
        case "ticket":
          return { company_id: { eq: parentId } };

        case "time_entries":
        case "time_entry":
        case "timeentries":
          return { company_id: { eq: parentId } };

        case "tasks":
        case "task":
          return { company_id: { eq: parentId } };

        case "notes":
        case "note":
          return { company_id: { eq: parentId } };

        case "communications":
        case "communication":
          return { company_id: { eq: parentId } };

        default:
          return null;
      }
    }

    default:
      return null;
  }
}

/**
 * Merges context filter with user-applied filters
 * Context filter is applied as a soft filter (can be overridden by user)
 *
 * @param contextFilter - Filter from parent entity context
 * @param userFilter - Filter applied by user
 * @returns Merged filter object
 */
export function mergeContextAndUserFilters(
  contextFilter: Record<string, any> | null,
  userFilter: Record<string, any> | null
): Record<string, any> | null {
  if (!contextFilter && !userFilter) {
    return null;
  }

  if (!contextFilter) {
    return userFilter;
  }

  if (!userFilter) {
    return contextFilter;
  }

  // Merge filters with AND logic
  // Both context filter and user filter must be satisfied
  return {
    and: [contextFilter, userFilter],
  };
}

/**
 * Checks if a filter contains context-based filtering
 * Used to determine if we should show the context filter badge
 *
 * @param filter - Filter object to check
 * @param parentEntity - Parent entity context
 * @returns true if filter contains context-based filtering
 */
export function isContextFilterActive(
  filter: Record<string, any> | null,
  parentEntity: ParentEntity | null | undefined
): boolean {
  if (!filter || !parentEntity?.id) {
    return false;
  }

  // Check if filter contains the parent entity ID
  const filterString = JSON.stringify(filter);
  return filterString.includes(parentEntity.id);
}
